import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  addMonths,
  getChartDataMonth,
  getChartDataTrimestre,
  getChartDataYear,
} from "@/libs/utils";
import { Indicator } from "@/types";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

const ChartGlobal: FC<{
  dataReporting: any;
  minDate: any;
  maxDate: any;
  indicator: Indicator;
  currentYear: number;
  maxTco2Year: number;
  option?: string;
  context?: any;
  isCumulative?: boolean;
  externalLoading?: boolean;
}> = ({
  dataReporting,
  minDate,
  maxDate,
  indicator,
  currentYear,
  maxTco2Year,
  option,
  context = null,
  isCumulative,
  externalLoading = false,
}) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "program",
  });
  const chartConfig = {
    prevu: {
      label: t("Prévu"),
      color: "hsl(var(--color-blue))",
    },
    realise: {
      label: t("Réalisé"),
      color: "hsl(var(--color-green))",
    },
    verifie: {
      label: t("Verifié / justifié / certifié"),
      color: "hsl(var(--color-orange))",
    },
  } satisfies ChartConfig;

  const launchDate = new Date(minDate + "-01-01").getFullYear();
  const expectedDuration = (maxDate - minDate) * 12;
  const dateWithAddedMonths = addMonths(
    new Date(minDate + "-01-01"),
    expectedDuration
  );
  const expectedDurationYear = !dateWithAddedMonths
    ? 0
    : dateWithAddedMonths?.getFullYear() == launchDate
      ? 1
      : dateWithAddedMonths.getFullYear() - (launchDate - 1);

  const indicatorForChart = useMemo(
    () => ({
      ...indicator,
      cumulativeDashboard:
        context === "dashboard"
          ? (isCumulative ?? (indicator as any)?.cumulativeDashboard ?? (indicator as any)?.cumulative ?? false)
          : (indicator as any)?.cumulativeDashboard,
      cumulativeProgram:
        context === "programme"
          ? (isCumulative ?? (indicator as any)?.cumulativeProgram ?? (indicator as any)?.cumulative ?? false)
          : (indicator as any)?.cumulativeProgram,
      cumulativeReporting:
        context !== "dashboard" && context !== "programme"
          ? (isCumulative ?? (indicator as any)?.cumulativeReporting ?? (indicator as any)?.cumulative ?? false)
          : (indicator as any)?.cumulativeReporting,
    }),
    [indicator, isCumulative, context]
  );

  const reportingDataForChart = useMemo(
    () =>
      (dataReporting ?? []).map((item: any) => ({
        ...item,
        indicator: {
          ...(item.indicator as Record<string, unknown>),
          cumulativeDashboard: indicatorForChart.cumulativeDashboard,
          cumulativeProgram: indicatorForChart.cumulativeProgram,
          cumulativeReporting: indicatorForChart.cumulativeReporting,
        },
      })),
    [
      dataReporting,
      indicatorForChart.cumulative,
      indicatorForChart.cumulativeDashboard,
      indicatorForChart.cumulativeProgram,
      indicatorForChart.cumulativeReporting,
      isCumulative,
    ]
  );

  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const computeData = () => {
      if (reportingDataForChart == null) {
        if (isActive) {
          setData(null);
          setIsLoading(false);
        }
        return;
      }

      if (isActive) {
        setData(null);
      }
      setIsLoading(true);

      let nextData: any[] | null = null;
      switch (option) {
        case "1":
          nextData = getChartDataMonth(
            reportingDataForChart,
            currentYear,
            indicatorForChart,
            context
          );
          break;
        case "2":
          nextData = getChartDataTrimestre(
            reportingDataForChart,
            expectedDurationYear,
            indicatorForChart,
            launchDate,
            context
          );
          break;
        case "3":
          nextData = getChartDataYear(
            reportingDataForChart,
            indicatorForChart,
            expectedDurationYear,
            launchDate,
            minDate,
            indicator.code === "tco2_sequestrees" ? maxTco2Year : maxDate,
            context
          );
          break;
        default:
          nextData = [];
      }

      const sanitizedData = Array.isArray(nextData)
        ? nextData.map((item) => ({
            ...item,
            prevu: Number.isFinite(Number(item?.prevu)) ? Number(item.prevu) : 0,
            realise: Number.isFinite(Number(item?.realise))
              ? Number(item.realise)
              : 0,
            verifie: Number.isFinite(Number(item?.verifie))
              ? Number(item.verifie)
              : 0,
          }))
        : nextData;

      if (isActive) {
        setData(sanitizedData);
        setIsLoading(false);
      }
    };

    computeData();

    return () => {
      isActive = false;
    };
  }, [
    option,
    reportingDataForChart,
    currentYear,
    indicatorForChart,
    context,
    expectedDurationYear,
    launchDate,
    minDate,
    maxDate,
    maxTco2Year,
  ]);

  return (
    <div className="relative min-h-[200px] w-full">
      {(externalLoading || isLoading || data == null) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[20px] bg-card/70 backdrop-blur-[1px]">
          <LoadingSpinner />
        </div>
      )}
      {data != null && !isLoading && !externalLoading && (
        <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
          <BarChart accessibilityLayer data={data as any} barGap={2}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="legend"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickCount={4}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y + 10}
                  textAnchor="middle"
                  fill="#000"
                  fontSize={12}
                >
                  {Number(payload.value)
                    .toLocaleString("fr-FR")
                    .replace(/\u202F/g, " ")}
                </text>
              )}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="prevu" fill={chartConfig.prevu.color} radius={20} />
            <Bar dataKey="realise" fill={chartConfig.realise.color} radius={20} />
            <Bar dataKey="verifie" fill={chartConfig.verifie.color} radius={20} />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
};

export default ChartGlobal;
